import { createSlice } from "@reduxjs/toolkit";
import { DEFAULT_GATE_MODE } from "constants/proposals";

export const INITIAL_SORTING = { field: "trackedDate", direction: "DESC" };
export const INITIAL_BEST_FIT_SORTING = { value: "overallFit" };

export const INITIAL_STATE = {
  data: [],
  isBestFit: false,
  isLoading: false,
  isInitialized: false,
  isCountInitialized: false,
  searchBy: "",
  selectedRowIndex: -1,
  bestFitSortBy: INITIAL_BEST_FIT_SORTING,
  sorting: INITIAL_SORTING,
  currentCount: 0,
  totalCount: 0,
  lastUpdatedValue: 0,
  isCustomLastUpdated: false,
  additionalFilters: {},
  kanbanColumns: [],
  isKanbanStale: false,
  kanbanVersion: 0,
  gateMode: DEFAULT_GATE_MODE,
};

const removeFromKanbanColumns = (state, opportunityId) => {
  state.kanbanColumns.forEach((column) => {
    column.items = (column.items || []).filter(({ id }) => id !== opportunityId);
  });
};

const trackedSlice = createSlice({
  name: "tracked",
  initialState: INITIAL_STATE,
  reducers: {
    removeById(state, { payload }) {
      state.data = state.data.filter(({ id }) => id !== payload);
      removeFromKanbanColumns(state, payload);
    },
    archiveById(state, { payload }) {
      state.data = state.data.filter(({ id }) => id !== payload);
      removeFromKanbanColumns(state, payload);
      state.totalCount = Math.max(0, state.totalCount - 1);
      state.currentCount = Math.max(0, state.currentCount - 1);
    },
    reset() {
      return INITIAL_STATE;
    },
    setData(state, { payload }) {
      state.data = payload || [];
    },
    setIsBestFit(state, { payload }) {
      state.isBestFit = Boolean(payload);
    },
    setCurrentCount(state, { payload }) {
      state.currentCount = payload || 0;
    },
    setTotalCount(state, { payload }) {
      state.totalCount = payload || 0;
    },
    setIsLoading(state, { payload }) {
      state.isLoading = Boolean(payload);
    },
    setIsInitialized(state, { payload }) {
      state.isInitialized = Boolean(payload);
    },
    setIsCountInitialized(state, { payload }) {
      state.isCountInitialized = Boolean(payload);
    },
    setSelectedRowIndex(state, { payload }) {
      state.selectedRowIndex = payload ?? -1;
    },
    setBestFitSortBy(state, { payload }) {
      state.bestFitSortBy = payload || INITIAL_BEST_FIT_SORTING;
    },
    setSearchBy(state, { payload }) {
      state.searchBy = payload ?? "";
    },
    setSorting(state, { payload }) {
      state.sorting = payload || INITIAL_SORTING;
    },
    setLastUpdatedValue(state, { payload }) {
      state.lastUpdatedValue = payload || 0;
    },
    setIsCustomLastUpdated(state, { payload }) {
      state.isCustomLastUpdated = payload;
    },
    setAdditionalFilters(state, { payload }) {
      state.additionalFilters = payload || {};
    },
    setKanbanColumns(state, { payload }) {
      state.kanbanColumns = payload || [];
      state.isKanbanStale = false;
    },
    markKanbanStale(state) {
      state.isKanbanStale = true;
      state.kanbanVersion += 1;
    },
    updateKanbanItemAssignees(state, { payload: { itemId, assignees } }) {
      state.kanbanColumns.forEach((column) => {
        const item = (column.items || []).find((row) => row.id === itemId);
        if (item) {
          item.assignees = assignees || [];
        }
      });
    },
    updateListItemAssignees(state, { payload: { itemId, assignees } }) {
      const item = state.data.find((row) => row.id === itemId);
      if (item) {
        item.assignees = assignees || [];
      }
    },
    setGateMode(state, { payload }) {
      state.gateMode = payload;
    },
  },
});

export const {
  removeById,
  archiveById,
  reset,
  setData,
  setCurrentCount,
  setTotalCount,
  setIsBestFit,
  setIsLoading,
  setIsInitialized,
  setIsCountInitialized,
  setSelectedRowIndex,
  setBestFitSortBy,
  setSearchBy,
  setSorting,
  setLastUpdatedValue,
  setAdditionalFilters,
  setIsCustomLastUpdated,
  setKanbanColumns,
  markKanbanStale,
  updateKanbanItemAssignees,
  updateListItemAssignees,
  setGateMode,
} = trackedSlice.actions;

export default trackedSlice;
